import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { DialogTrigger, Heading, Text } from 'react-aria-components';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import Modal from 'src/components/ui/Modal';
import FeedbackDialog from 'src/components/FeedbackDialog';
import { graphQLErrorNotification } from 'src/utils/notifications';
import MailIcon from 'src/assets/icons/envelope.svg';
import ArrowRightIcon from 'src/assets/icons/arrow-right-1.svg';
import MAGIC_LOGIN_MUTATION from 'src/graphql/mutation/magic-login.graphql';
import { ENV_MODE, isWhiteLabel } from 'src/utils';

const CreatorSignInPage = () => {
  useEffect(() => {
    document.body.classList.add('login');

    return () => {
      document.body.classList.remove('login');
    };
  }, []);

  return <SignInForm />;
};

const SignInSchema = z.object({
  email: z.string().email(),
});

type SignInSchemaType = z.infer<typeof SignInSchema>;

const SignInForm = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [noAccount, setNoAccount] = useState(false);

  const { handleSubmit, clearErrors, reset, control } =
    useForm<SignInSchemaType>({
      resolver: zodResolver(SignInSchema),
      defaultValues: {
        email: '',
      },
    });

  const [magicLogin, { loading }] = useMutation(MAGIC_LOGIN_MUTATION, {
    onCompleted: async (data, clientOptions) => {
      if (data.magicLogin) {
        const email = clientOptions?.variables?.input?.email;

        await navigate({
          to: '/magic-link',
          replace: true,
          search: { email },
        });
      }
    },
    onError: (error) => {
      const errorKeys = (error?.graphQLErrors ?? []).flatMap((ge) =>
        Object.keys(ge?.extensions?.fields ?? {})
      );

      const hasNoAccount = errorKeys.includes(NO_ACCOUNT_ERROR_CODE);

      if (hasNoAccount) {
        setNoAccount(hasNoAccount);
        return null;
      }

      return graphQLErrorNotification(error);
    },
  });

  const onSubmit = useCallback(
    async ({ email = '' }) => {
      const recaptcha = executeRecaptcha
        ? await executeRecaptcha('login')
        : null;
      const input = { email, recaptcha };

      clearErrors();
      await magicLogin({
        variables: {
          input,
          whitelabelKey: isWhiteLabel ? ENV_MODE : null,
          role: 'CREATOR',
        },
      });
      reset();
    },
    [magicLogin, clearErrors, reset, executeRecaptcha]
  );

  if (noAccount) {
    return (
      <div>
        <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
          No Account Found!
        </Heading>
        <Text
          elementType="p"
          className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
        >
          Looks like you do not have an account. Please use the form below to
          request access.
        </Text>
        <RequestAccessButton onClose={() => setNoAccount(false)} />
      </div>
    );
  }

  return (
    <div>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Welcome to the Creator Studio!
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
      >
        Enter your email address to access your account.
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              {...field}
              Icon={MailIcon}
              label="Email"
              placeholder="john@gmail.com"
              validationBehavior="aria"
              isInvalid={invalid}
              errorMessage={error?.message}
              isRequired
              autoComplete="email"
            />
          )}
        />
        <Button
          className="w-full justify-center mt-8 gap-2"
          type="submit"
          isDisabled={loading}
          loading={loading}
        >
          Continue
          <ArrowRightIcon className="w-3 h-3 text-primary-45" />
        </Button>
      </form>
    </div>
  );
};

export const RequestAccessButton = ({
  className,
  onClose,
}: {
  className?: string;
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({
    query: '(max-width: 768px)',
  });

  const size = isMobile ? 'small' : 'medium';

  return (
    <DialogTrigger>
      <Button ghost variant="success" size="medium" className={className}>
        Request Access
      </Button>
      <Modal className="max-w-[37.5rem]" size={size} isKeyboardDismissDisabled>
        <FeedbackDialog size={size} onClose={onClose} />
      </Modal>
    </DialogTrigger>
  );
};

const NO_ACCOUNT_ERROR_CODE = 'register_creator';

export const Route = createFileRoute('/_auth/creator-login')({
  component: CreatorSignInPage,
});
